import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, type ReactElement } from 'react';
import { AddRepertoireSearchPane } from '@/components/organisms/add-repertoire-search-pane';
import { AddRepertoireFormPane } from '@/components/organisms/add-repertoire-form-pane';
import { DiscardChangesDialog } from '@/components/molecules/discard-changes-dialog';

const EMPTY_RIGHT_PANE_COPY = 'Pick a tune from the library to add it to your repertoire.';

type AddRepertoireSearch = {
  pieceId?: string;
};

export const Route = createFileRoute('/repertoire_/add')({
  component: AddRepertoirePage,
  validateSearch: (search: Record<string, unknown>): AddRepertoireSearch => ({
    pieceId: typeof search.pieceId === 'string' ? search.pieceId : undefined,
  }),
});

function AddRepertoirePage(): ReactElement {
  const { pieceId } = Route.useSearch();
  const navigate = useNavigate({ from: '/repertoire/add' });
  const [isFormDirty, setIsFormDirty] = useState<boolean>(false);
  const [pendingPieceId, setPendingPieceId] = useState<string | null>(null);

  const navigateToPiece = (nextPieceId: string): void => {
    void navigate({
      search: (prev) => ({ ...prev, pieceId: nextPieceId }),
      replace: true,
    });
  };

  const handleSelectPiece = (nextPieceId: string): void => {
    if (isFormDirty && pieceId !== undefined && nextPieceId !== pieceId) {
      setPendingPieceId(nextPieceId);
      return;
    }
    navigateToPiece(nextPieceId);
  };

  const handleConfirmDiscard = (): void => {
    if (pendingPieceId === null) return;
    const target = pendingPieceId;
    setPendingPieceId(null);
    setIsFormDirty(false);
    navigateToPiece(target);
  };

  const handleKeepEditing = (): void => {
    setPendingPieceId(null);
  };

  return (
    <div className="hidden min-h-screen bg-[var(--bg-page)] text-[var(--ink)] lg:grid lg:grid-cols-[minmax(360px,420px)_1fr]">
      <section
        role="search"
        aria-label="Search the library"
        className="flex min-h-screen flex-col border-r border-[var(--rule)] bg-[var(--bg-surface)]"
      >
        <AddRepertoireSearchPane
          selectedPieceId={pieceId}
          onSelectPiece={handleSelectPiece}
        />
      </section>

      <section
        role="region"
        aria-label="Selected piece"
        data-piece-id={pieceId}
        className="flex min-h-screen flex-col"
      >
        {pieceId === undefined ? (
          <div className="flex flex-1 items-center justify-center px-10 py-10 text-center">
            <p className="max-w-md text-sm text-[var(--ink-3)]">{EMPTY_RIGHT_PANE_COPY}</p>
          </div>
        ) : (
          <AddRepertoireFormPane pieceId={pieceId} onDirtyChange={setIsFormDirty} />
        )}
      </section>

      {pendingPieceId !== null && (
        <DiscardChangesDialog
          onConfirm={handleConfirmDiscard}
          onCancel={handleKeepEditing}
        />
      )}
    </div>
  );
}
